import endpoint from "#src/config/endpoint";
import request from "#src/utils/request";
import type { IPaginatedResponse, IPaginateParams } from "#src/@types/api";
import {
  IPricingInput,
  IPricingInfo,
  IPromotionCodeResponse,
  IPaymentForm,
  IPaymentInfo,
  IWorkspacePaymentSettings,
  IWorkspacePaymentPlanItem,
  IPaymentOverview,
  IPaymentItem,
  IPaymentContract,
} from "./types";

function calculatePricing(workspace_id: string, data: IPricingInput) {
  return request.post<never, IPricingInfo>(
    `${endpoint.WORKSPACES}/${workspace_id}/payments/calc`,
    data
  );
}

function checkPromotionCode(
  workspace_id: string,
  data: { promo_code: string }
) {
  return request.post<never, IPromotionCodeResponse>(
    `${endpoint.WORKSPACES}/${workspace_id}/payments/promo_code/calc`,
    data
  );
}

function createSubscriptionBanking(
  workspace_id: string,
  data:
    | IPaymentForm
    | {
        product_plans: {
          product_plan_id: string;
          duration: string;
          promo_code?: string;
        }[];
      }
) {
  // there's a case the request will return { only_free_plan: true, success: true }
  return request.post<never, IPaymentInfo>(
    `${endpoint.WORKSPACES}/${workspace_id}/payments/subscriptions`,
    data
  );
}

function retrievePayment(workspace_id: string, payment_id: string) {
  return request.get<never, IPaymentInfo>(
    `${endpoint.WORKSPACES}/${workspace_id}/payments/${payment_id}`
  );
}

function retrieveWorkspacePaymentSettings(workspace_id: string) {
  return request.get<never, IWorkspacePaymentSettings>(
    `${endpoint.WORKSPACES}/${workspace_id}/payments/setting`
  );
}

function updateWorkspacePaymentSettings(
  workspace_id: string,
  data: IWorkspacePaymentSettings
) {
  return request.put<never, IWorkspacePaymentSettings>(
    `${endpoint.WORKSPACES}/${workspace_id}/payments/setting`,
    data
  );
}

/** Return list of currently active services that will need to be paid. */
function listWorkspacePaymentPlans(
  workspaceId: string,
  params: {
    status: string | undefined;
    payment_method: string | undefined;
    q?: string;
  }
) {
  return request.get<never, IWorkspacePaymentPlanItem[]>(
    `${endpoint.WORKSPACES}/${workspaceId}/payments/plans`,
    { params: { ...params, paging: 0 } }
  );
}

function retrieveWorkspacePaymentOverview(workspace_id: string) {
  return request.get<never, IPaymentOverview>(
    `${endpoint.WORKSPACES}/${workspace_id}/payments/overview`
  );
}

function renewSubscriptionBanking(workspace_id: string, data: IPaymentForm) {
  return request.post<never, IPaymentInfo>(
    `${endpoint.WORKSPACES}/${workspace_id}/payments/renewal`,
    data
  );
}

function listPayments(
  workspaceId: string,
  params: IPaginateParams & {
    from?: string;
    to?: string;
    status: string | undefined;
    payment_method: string | undefined;
  }
) {
  return request.get<never, IPaginatedResponse<IPaymentItem>>(
    `${endpoint.WORKSPACES}/${workspaceId}/payments`,
    { params }
  );
}

function listContracts(
  workspaceId: string,
  params: IPaginateParams & { status: string | undefined }
) {
  return request.get<never, IPaginatedResponse<IPaymentContract>>(
    `${endpoint.WORKSPACES}/${workspaceId}/payments/contracts`,
    { params }
  );
}

function upgradeSubscription(workspaceId: string, data: IPaymentForm) {
  return request.post<never, IPaymentInfo>(
    `${endpoint.WORKSPACES}/${workspaceId}/payments/upgrade`,
    data
  );
}

function cancelProductPlan(workspaceId: string, productId: number) {
  return request.post<never, any>(
    `${endpoint.WORKSPACES}/${workspaceId}/payments/plans/${productId}/cancel`,
    { params: { immediately: false } }
  );
}

const paymentService = {
  calculatePricing,
  checkPromotionCode,
  createSubscriptionBanking,
  retrievePayment,
  retrieveWorkspacePaymentSettings,
  updateWorkspacePaymentSettings,
  listWorkspacePaymentPlans,
  retrieveWorkspacePaymentOverview,
  renewSubscriptionBanking,
  listPayments,
  listContracts,
  upgradeSubscription,
  cancelProductPlan,
};

export default paymentService;
